import { Pagination, Skeleton, ThemeProvider } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import SessionsApi from "../../api/sessionApi";
import ModalWindow from "../../components/ModalWindow/ModalWindow";
import StandartButton from "../../components/StandartButton/StandartButton";
import { SessionPackage } from "../../types/TypeSession";
import { theme } from "../../utils/theme";
import Notify, { SuccesNotify } from "../../utils/toaster";
import PackageCard from "./PackageCard/PackageCard";
import s from "./SessionPackages.module.css";

const SessionPackages = () => {
  const token = localStorage.getItem("token");
  const [packagesData, setPackagesData] = useState<SessionPackage[]>([]);
  const [loaders, setLoaders] = useState({
    fetchLoader: false,
    actionLoader: false,
  });
  const [activePackageId, setActivePackageId] = useState<string | undefined>();
  const [reasonModal, setReasonModal] = useState<{
    modal: boolean;
    packageId?: string;
    action?: "deny" | "cancel";
    reason: string;
  }>({ modal: false, reason: "" });
  const [paggination, setPaggination] = useState({ page: 1, totalPages: 0 });
  const packagesLimit = 5;

  const getUnapprovedPackages = useCallback(
    async (page: number) => {
      if (!token) return;
      setLoaders((prev) => ({ ...prev, fetchLoader: true }));

      const response = await SessionsApi.getUnapprovedPackages(
        token,
        page,
        packagesLimit,
      );

      setLoaders((prev) => ({ ...prev, fetchLoader: false }));
      if (response.status && response.packages) {
        setPaggination((prev) => ({
          ...prev,
          totalPages: Math.ceil((response.totalAmount ?? 0) / packagesLimit),
        }));
        setPackagesData(response.packages);
      }
    },
    [token],
  );

  useEffect(() => {
    getUnapprovedPackages(paggination.page);
  }, [paggination.page]);

  const approvePackage = async (packageId: string) => {
    if (!token) return;
    setActivePackageId(packageId);
    setLoaders((prev) => ({ ...prev, actionLoader: true }));
    const response = await SessionsApi.approvePackageVerification(
      token,
      packageId,
    );
    setLoaders((prev) => ({ ...prev, actionLoader: false }));
    setActivePackageId(undefined);
    if (response.status) {
      SuccesNotify("Package approved and creator notified");
      setTimeout(() => {
        setPackagesData((prevPackages) =>
          prevPackages.filter((pack) => pack._id !== packageId),
        );
      }, 500);
      return;
    }
    Notify(response.message || "Failed to approve package");
  };

  const openReasonModal = (packageId: string, action: "deny" | "cancel") => {
    setReasonModal({ modal: true, packageId, action, reason: "" });
  };

  const submitReasonAction = async () => {
    if (!token || !reasonModal.packageId || !reasonModal.action) return;
    setActivePackageId(reasonModal.packageId);
    setLoaders((prev) => ({ ...prev, actionLoader: true }));

    const reason = reasonModal.reason.trim() || undefined;
    const response =
      reasonModal.action === "deny"
        ? await SessionsApi.denyPackageVerification(
            token,
            reasonModal.packageId,
            reason,
          )
        : await SessionsApi.cancelPackageVerification(
            token,
            reasonModal.packageId,
            reason,
          );

    setLoaders((prev) => ({ ...prev, actionLoader: false }));
    setActivePackageId(undefined);

    if (!response.status) {
      Notify(response.message || "Failed to update package review request");
      return;
    }

    SuccesNotify(
      reasonModal.action === "deny"
        ? "Package verification denied and creator notified"
        : "Package verification request canceled",
    );
    setPackagesData((prevPackages) =>
      prevPackages.filter((pack) => pack._id !== reasonModal.packageId),
    );
    setReasonModal({ modal: false, reason: "" });
  };

  return (
    <div className={s.container}>
      <div className={s.headerBlock}>
        <h1 className={s.pageTitle}>Package review requests</h1>
        <p className={s.pageDescription}>
          Only packages explicitly submitted for verification appear here.
        </p>
      </div>
      {packagesData && packagesData.length ? (
        loaders.fetchLoader ? (
          Array.from({ length: 5 }, (_, index) => (
            <div className={s.skeletonBlock} key={index}>
              <Skeleton
                height={370}
                width={1200}
                variant="rectangular"
                sx={{ borderRadius: "20px" }}
              />
            </div>
          ))
        ) : (
          <TransitionGroup component={null}>
            {packagesData.map((item) => (
              <CSSTransition
                key={item._id}
                timeout={500}
                classNames={{
                  enter: s.packageEnter,
                  enterActive: s.packageEnterActive,
                  exit: s.packageExit,
                  exitActive: s.packageExitActive,
                }}
              >
                <div className={s.packageElement}>
                  <PackageCard
                    sessionPackage={item}
                    actionLoader={
                      loaders.actionLoader && activePackageId === item._id
                    }
                    approvePackage={approvePackage}
                    denyPackage={(packageId) =>
                      openReasonModal(packageId, "deny")
                    }
                  />
                </div>
              </CSSTransition>
            ))}
          </TransitionGroup>
        )
      ) : (
        <h2 className={s.noPostTitle}>No package review requests</h2>
      )}
      {packagesData && packagesData.length > 0 && (
        <div className={s.pagginationBlock}>
          <ThemeProvider theme={theme}>
            <Pagination
              count={paggination.totalPages}
              page={paggination.page}
              color="primary"
              sx={{
                "& .MuiPaginationItem-root": {
                  color: "white", // цифры и стрелки
                },
                "& .MuiPaginationItem-root.Mui-selected": {
                  color: "white", // выбранная цифра
                  backgroundColor: "primary.main", // фон для выбранного
                },
              }}
              onChange={(_, value) =>
                setPaggination((prev) => ({ ...prev, page: value }))
              }
            />
          </ThemeProvider>
        </div>
      )}
      <ModalWindow
        isOpen={reasonModal.modal}
        alternativeCloseFunction={() =>
          setReasonModal({ modal: false, reason: "" })
        }
        width="min(520px, 95vw)"
        title={
          reasonModal.action === "deny"
            ? "Deny verification"
            : "Cancel verification request"
        }
      >
        <div className={s.reasonModal}>
          <p className={s.reasonText}>
            {reasonModal.action === "deny"
              ? "Explain what should be fixed. The reason is optional but recommended."
              : "Add an optional cancellation reason."}
          </p>
          <textarea
            className={s.reasonInput}
            value={reasonModal.reason}
            onChange={(event) =>
              setReasonModal((prev) => ({
                ...prev,
                reason: event.target.value,
              }))
            }
            placeholder="Reason"
          />
          <div className={s.reasonActions}>
            <button
              className={s.secondaryBtn}
              onClick={() => setReasonModal({ modal: false, reason: "" })}
              disabled={loaders.actionLoader}
            >
              Cancel
            </button>
            <StandartButton
              buttonTitle={
                reasonModal.action === "deny"
                  ? "Deny verification"
                  : "Cancel request"
              }
              action={submitReasonAction}
              disabled={loaders.actionLoader}
            />
          </div>
        </div>
      </ModalWindow>
    </div>
  );
};

export default SessionPackages;
